import { useEffect, useState } from 'react';
import type { ApolloClient } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import Storyblok from '../../content/storyblok/storyblok';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useContentClient from '../../content/hooks/useContentClient';
import getPublishedDate from '../utils/getPublishedDate';
import { STORYBLOK_ERROR_MESSAGE } from '../../content/constants';

// cspell:words PostcategoryItems
const postCategoriesQuery = gql`
    query postCategoriesQuery($start_with: String!) {
        PostCategoryItems: PostcategoryItems(starts_with: $start_with) {
            items {
                content {
                    name
                }
                uuid
            }
        }
    }
`;

const postPagesForCategoryQuery = gql`
    query postCategoriesQuery(
        $start_with: String!
        $filter: PostFilterQuery
        $first_published_at_lt: String
    ) {
        PostItems(
            starts_with: $start_with
            filter_query_v2: $filter
            first_published_at_lt: $first_published_at_lt
        ) {
            total
        }
    }
`;

export interface PostsCategory {
    name: string;
    id: string;
    total: number;
}

interface PostCategoryItem {
    content: {
        name: string;
    };
    uuid: string;
}

const loadCountForCategories = async ({
    store,
    language,
    items,
    client,
}: {
    store: string;
    language: string;
    items: PostCategoryItem[];
    client: ApolloClient<unknown>;
}) => {
    return Promise.all(
        items.map(async category => {
            const { data: CategoryCountData } = await client.query<
                {
                    PostItems: {
                        total: number;
                    };
                },
                {
                    start_with: string;
                    filter: {
                        category: {
                            in: string;
                        };
                    };
                    first_published_at_lt: string;
                }
            >({
                query: postPagesForCategoryQuery,
                variables: {
                    start_with: Storyblok.formatSlug(store, language, 'news/'),
                    first_published_at_lt: getPublishedDate(),
                    filter: {
                        category: {
                            in: category.uuid,
                        },
                    },
                },
            });

            return {
                total: CategoryCountData.PostItems.total,
                name: category.content.name,
                id: category.uuid,
            };
        })
    );
};

const usePostsCategoriesWithCount = () => {
    const { store, language } = useStoreAndLanguageParams();

    const client = useContentClient();

    const { data, error } = useQuery<
        {
            PostCategoryItems: {
                items: PostCategoryItem[];
            };
        },
        {
            start_with: string;
        }
    >(postCategoriesQuery, {
        variables: {
            start_with: Storyblok.formatSlug(
                store,
                language,
                'news/categories'
            ),
        },
        client,
    });

    const [categories, setCategories] = useState<PostsCategory[] | undefined>(
        undefined
    );

    useEffect(() => {
        const loadData = async () => {
            if (data?.PostCategoryItems) {
                const result = await loadCountForCategories({
                    store,
                    language,
                    items: data.PostCategoryItems.items,
                    client,
                });
                setCategories(result.filter(category => category.total));
            }
        };
        loadData().catch(ex => {
            throw ex;
        });
    }, [data, language, store, client]);

    if (error) {
        throw new Error(STORYBLOK_ERROR_MESSAGE, { cause: error });
    }

    return categories;
};

export default usePostsCategoriesWithCount;
